/**
 * ALERON product page.
 *
 * Three jobs:
 *   1. Variant selection — price, availability, the ADD button and the URL.
 *   2. Adding to the cart, then opening the cart drawer.
 *   3. The mobile gallery's progress bar.
 *
 * Variant data is serialised into the page as JSON rather than fetched, so
 * choosing a size never waits on the network.
 */

import { CartLinesUpdateEvent } from '@shopify/events';
import { AleronOverlay, define, openPanel } from '@theme/aleron-overlay';
import { formatMoneyFromCents } from '@theme/aleron-money';

function themeRoutes() {
  return window.Theme?.routes ?? {};
}

class AleronProduct extends HTMLElement {
  connectedCallback() {
    this.variants = this.#readVariants();
    this.form = this.querySelector('[data-aleron-product-form]');
    this.idInput = this.querySelector('[data-aleron-variant-id]');
    this.addButton = this.querySelector('[data-aleron-add]');

    this.addEventListener('click', this.#handleClick);
    this.form?.addEventListener('submit', this.#handleSubmit);

    // Initial sync only reflects what the server already rendered, so it
    // must not touch the URL.
    this.#syncFromSelection({ updateUrl: false });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.form?.removeEventListener('submit', this.#handleSubmit);
  }

  #readVariants() {
    const script = this.querySelector('[data-aleron-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch {
      return [];
    }
  }

  #handleClick = (event) => {
    const option = event.target instanceof Element ? event.target.closest('[data-aleron-option]') : null;
    if (!option || option.hasAttribute('aria-disabled')) return;

    event.preventDefault();

    const group = option.dataset.aleronOption;
    for (const sibling of this.querySelectorAll(`[data-aleron-option="${CSS.escape(group)}"]`)) {
      sibling.setAttribute('aria-pressed', String(sibling === option));
    }

    this.#syncFromSelection({ updateUrl: true });
  };

  /** Current value picked for each option position. */
  #selection() {
    const values = [];
    for (const group of this.querySelectorAll('[data-aleron-option-group]')) {
      const position = Number(group.dataset.aleronOptionGroup);
      const pressed = group.querySelector('[aria-pressed="true"]');
      values[position - 1] = pressed?.dataset.value ?? null;
    }
    return values;
  }

  #syncFromSelection({ updateUrl = true } = {}) {
    const selection = this.#selection();

    // A variant matches only when every chosen value matches. Positions still
    // unchosen are skipped so a partial selection can't match the wrong row.
    const variant = this.variants.find((candidate) =>
      selection.every((value, i) => value == null || candidate.options[i] === value)
    );

    this.#markUnavailable(selection);

    if (!variant) {
      this.#setAddState({ available: false, label: this.dataset.labelUnavailable });
      return;
    }

    if (this.idInput) this.idInput.value = String(variant.id);
    this.#setPrice(variant);
    this.#setAddState({
      available: variant.available,
      label: variant.available ? this.dataset.labelAdd : this.dataset.labelSoldOut
    });

    // Keep the address bar on the chosen variant so a copied link, a refresh
    // or a share all land on the same size — but only once the shopper has
    // actually chosen. Rewriting on first load changes the URL out from
    // under someone who has done nothing, and makes a plain product link
    // silently become a variant link on arrival.
    if (updateUrl && variant.url && window.history?.replaceState) {
      window.history.replaceState({}, '', variant.url);
    }

    this.dispatchEvent(new CustomEvent('aleron:variant', { bubbles: true, detail: { variant } }));
  }

  /**
   * Strikes through the values that cannot be bought given what else is
   * chosen, rather than only the ones that are globally sold out.
   */
  #markUnavailable(selection) {
    for (const group of this.querySelectorAll('[data-aleron-option-group]')) {
      const index = Number(group.dataset.aleronOptionGroup) - 1;

      for (const option of group.querySelectorAll('[data-aleron-option]')) {
        const candidate = selection.slice();
        candidate[index] = option.dataset.value;

        const reachable = this.variants.some(
          (variant) =>
            variant.available &&
            candidate.every((value, i) => value == null || variant.options[i] === value)
        );

        option.classList.toggle('aleron-size--soldout', !reachable);
        option.toggleAttribute('aria-disabled', !reachable);
      }
    }
  }

  #setPrice(variant) {
    // Variant prices come from the Cart API's units: cents.
    const format = (cents) => formatMoneyFromCents(cents, this.dataset.moneyFormat);

    const current = this.querySelector('[data-aleron-price]');
    const was = this.querySelector('[data-aleron-compare-price]');

    if (current) current.textContent = format(variant.price);

    if (was) {
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      was.textContent = onSale ? format(variant.compare_at_price) : '';
      was.hidden = !onSale;
    }
  }

  #setAddState({ available, label }) {
    if (!this.addButton) return;
    this.addButton.disabled = !available;
    this.addButton.setAttribute('aria-disabled', String(!available));

    const text = this.addButton.querySelector('[data-aleron-add-label]');
    if (text && label) text.textContent = label;
  }

  #handleSubmit = async (event) => {
    event.preventDefault();
    if (this.busy || this.addButton?.disabled) return;

    const variantId = this.idInput?.value;
    if (!variantId) return;

    this.busy = true;
    this.setAttribute('data-busy', '');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise
      })
    );

    try {
      const response = await fetch(themeRoutes().cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });
      const result = await response.json();

      // A rejected add answers 422 with a `status` field instead of throwing.
      if (result.status) throw new Error(result.description || result.message || 'Add to cart failed');

      const cart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'aleron-product',
          sourceId: this.id || 'product',
          itemCount: 1,
          productId: this.dataset.productId,
          didError: false
        }
      });

      openPanel('cart-drawer');
    } catch (error) {
      deferred.reject(error);
      this.#showError(error);
    } finally {
      this.busy = false;
      this.removeAttribute('data-busy');
    }
  };

  #fetchCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems?.fetchCartData) return cartItems.fetchCartData();

    return fetch(`${themeRoutes().cart_url || '/cart'}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  #showError(error) {
    const region = this.querySelector('[data-aleron-product-error]');
    if (!region) return;
    region.textContent = error?.message || '';
    region.hidden = false;
  }
}

/**
 * The mobile gallery's progress bar, in place of dots.
 *
 * Driven by the scroll container's own position, so it tracks a half-finished
 * swipe rather than only snapping between whole slides.
 */
class AleronGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-aleron-gallery-track]');
    this.bar = this.querySelector('[data-aleron-gallery-progress]');
    if (!this.track || !this.bar) return;

    this.track.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#handleScroll();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.#handleScroll);
  }

  #handleScroll = () => {
    // rAF-throttled: scroll fires far more often than the bar needs redrawing.
    if (this.pending) return;
    this.pending = true;

    requestAnimationFrame(() => {
      this.pending = false;

      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const scrollable = scrollWidth - clientWidth;

      // A single-image gallery has nothing to scroll; show a full bar.
      const ratio = scrollable > 0 ? scrollLeft / scrollable : 1;
      const count = this.track.children.length || 1;

      // The bar is one slide wide and travels the remaining width, so its
      // length reads as "how much of the set is on screen".
      const width = 100 / count;
      this.bar.style.setProperty('--aleron-gallery-width', `${width}%`);
      this.bar.style.setProperty('--aleron-gallery-offset', `${ratio * (100 - width)}%`);

      const index = Math.round(ratio * (count - 1)) + 1;
      this.bar.setAttribute('aria-valuenow', String(index));
    });
  };
}

/** Size guide. Content comes from a page chosen in theme settings. */
class AleronSizeGuide extends AleronOverlay {}

define('aleron-product', AleronProduct);
define('aleron-gallery', AleronGallery);
define('aleron-size-guide', AleronSizeGuide);
